import Response from './response';
import {pathMatch} from './util.path-match';

import * as Router from './router';
import * as Info from './info';
import { Config, Extends } from './index';
import * as querystring from 'querystring';
import multipart, { File, saveFile, getUrl, getPath } from './server.multipart';
import * as http from 'http';
import { VirtualRequest } from './server.virtual';
import { ExtendedObject } from './info';

/**
 * 获取请求信息
 * @param	{String}	path	路径
 * @param	{String}	cbName	路径
 * @param	{Object}	query	查询信息
 * @param	{Object}	body 	请求体
 * @return	{Object}			请求信息
 */
function requestInfo(cbName: string, path: string, query: any): { paths: string[], data?: object, cb: string }{
	let data: object | undefined = undefined, cb: string = '';
	const paths = path
		.replace(/\.jsonp$/,x => (data = query, cb = data && data[cbName] || cbName, ''))
		.split(/[\\\/]/).filter(x => x);
	return { paths, data, cb};
}

/**
 * 解析获取路由信息
 * @param	{Array<String>}		path			路径数组
 * @param	{String | Function}	rf				模块名称或路由函数
 * @param	{String}			appId			应用ID
 * @param	{String}			method			采用的方法
 * @this	{Function}			getRouter		路由函数转换函数
 * @yield	{Function}							处理函数
 */
export function* funcRoute<Ext extends ExtendedObject = {user: object}>(
	path: string[],
	rf: string | Router.Router<Ext>,
	appId: string,
	method: string,
	getRouter: Router.get<Ext>,
	restful?: object
): IterableIterator<Router.ExecFunc<Ext>> {
	//如果是名称，则需要通过名称获取路由函数
	if (typeof rf === 'string' && getRouter) {
		rf = getRouter(rf, appId);
	}
	//如果路由不是函数，则跳过
	if (typeof rf !== 'function') { return; }
	//通过路由获取实际函数
	let f = rf(path, method, restful);
	if (!f) { return; }
	if (Symbol.iterator in f) {
		for (let sf of f) {
			if (typeof sf !== 'function') { continue; }
			yield sf;
		}
	} else if (typeof f === 'function') {
		yield f;
	}
}

export async function exec<Ext extends ExtendedObject = {user: object}>(
	extendsInt: Extends,
	func: Router.ExecFunc<Ext>,
	data: object,
	info: Info.Info<Ext>,
	appId: string,
	cb?: string
) {
	const response = new Response({cb, appId});
	info = Object.create(info);
	try {
		const ext = await extendsInt(appId);
		for (let key of ['app', 'extend', 'fs', 'db', 'config', 'log']) {
			if (key in ext) { info[key] = ext[key]; }
		}
	} catch(e) {}
	let ret;
	try {
		ret = await func(data, info, response);
	} catch(e) {
		if (e instanceof Response) { return response; }
		response.error = e;
		return response;
	}
	if (ret instanceof Response) {
		return ret;
	} else if (ret || typeof ret === 'string') {
		response.value = ret;
		return response;
	}
}

interface serverThis<Ext extends ExtendedObject = {user: object}> {
	extends: Extends;
	match: pathMatch;
	cbName: string;
	config: Config<Ext>;
	getRouter: Router.get<Ext>;
	getInfo: Info.createInfo<Ext>;
	saveFile: saveFile;
	getUrl: getUrl;
	getPath: getPath;
}

async function getStreamValue(stream): Promise<Buffer> {
	return new Promise((resolve, reject) => {
		let allData: Buffer[] = [];
		let size = 0;
		stream.on('error', reject);
		stream.on('data', function (data: Buffer) {
			allData.push(data);
			size += data.length;
		});
		stream.on('end', function () {
			return resolve(Buffer.concat(allData, size));
		});
	});
}

async function getBody( IncomingMessage: http.IncomingMessage, saveFile: saveFile, getUrl: getUrl, getPath: getPath): Promise<[object, File[]]> {
	let charset: string = '', ctype = IncomingMessage.headers['content-type'] || '';
	if (!ctype || ['GET', 'HEAD'].includes(IncomingMessage.method || '')) { return [{}, []]; }
	if (ctype) {
		[ctype, charset] = ctype.replace(/\s/g, '').split(';');
	}
	if (
		ctype === 'application/json' ||
		ctype === 'application/x-www-form-urlencoded' && (!charset || charset === 'charset=UTF-8')
	) {
		try {
			let data = await getStreamValue(IncomingMessage);
			if (!data.length) { return [{}, []]; }
			const text = data.toString();
			let body: object;
			if (ctype === 'application/json') {
				body = JSON.parse(text);
			} else {
				body = querystring.parse(text);
			}
			return [body, []];
		} catch (e) { }
		return [{}, []];
	}
	return await multipart(IncomingMessage, saveFile, getUrl, getPath);
}

async function getBodyData(request: http.IncomingMessage | VirtualRequest, saveFile: saveFile, getUrl: getUrl, getPath: getPath): Promise<[object, File[]]> {
	if ((request as VirtualRequest).isVirtualRequest) {
		return [(request as VirtualRequest).body, []];
	}
	return getBody(request as http.IncomingMessage, saveFile, getUrl, getPath);
}



export interface server {
	(
		path: string,
		query: object,
		IncomingMessage: http.IncomingMessage | VirtualRequest,
		opt: Info.Opt,
		saveFile?: saveFile,
		getUrl?: getUrl,
		getPath?: getPath
	): Promise<Response | void>;
}
/**
 * 动态处理
 * @param {String}		path					路径
 * @param {Object}		query					请求的query
 * @param {Object}		body					请求的body
 * @param {Object}		opt						userInfo的参数
 */
export async function server<Ext extends ExtendedObject = {user: object}>(
	this: serverThis<Ext> | void,
	path: string,
	query: object,
	request: http.IncomingMessage | VirtualRequest,
	opt: Info.Opt,
	saveFile?: saveFile,
	getUrl?: getUrl,
	getPath?: getPath
): Promise<Response | void> {
	let match = this && this.match || pathMatch;
	let cbName = this && this.cbName || 'cb';
	let router = this && this.getRouter || Router.get;
	let getInfo = this && this.getInfo || Info.createInfo;
	const extendsInt = this && this.extends || (() => ({}));
	saveFile = this && this.saveFile || saveFile;
	getUrl = this && this.getUrl || getUrl;
	getPath = this && this.getPath || getPath;

	//获取真是路径、数据及回调函数名称
	let {paths, data, cb} = requestInfo(cbName, path, query);
	let initData: (() => Promise<void>) = async () => {
		const [body, files] = await getBodyData(request, saveFile as saveFile, getUrl as getUrl, getPath as getPath);
		data = data && Object.keys(data).length && data || body && Object.keys(body).length && body || query || {};
		info.init(body, files, data);
		initData = () => Promise.resolve();
	}
	const info = await getInfo(opt, query);
	try {
		for await (let [p, rf, appId, restful] of match(paths, info)) {
			for (let func of funcRoute(paths.slice(p.length), rf, appId, opt.method, router, restful)) {
				await initData();
				const response = await exec(extendsInt, func, data as object, info, appId, cb);
				if (response) { return response; }
			}
		}
	} catch(error) {
		const response = new Response(cb);
		response.error = error;
		return response;
	} finally {
		for (let file of info.files) {
			file.destroy();
		}
	}
}

export default function init<Ext extends ExtendedObject = {user: object}>(
	config: Config<Ext>,
	match: pathMatch,
	getRouter: Router.get<Ext>,
	getInfo: Info.createInfo<Ext>,
): server {
	return server.bind({
		match,
		get extends(){ return config.extends; },
		get cbName() { return config.cbName; },
		saveFile(file: File){ return config.file.save(file); },
		getUrl(f: string){ return config.file.getUrl(f); },
		getPath(f: string){ return config.file.getPath(f); },
		getRouter, getInfo,
	} as serverThis<Ext>);
}